import { ForecastTableContentViewModel } from '../../baseTableContentViewModel';
import {
  ForecastForReceiptHeaderKey,
  isCustomerAnnounceForReceiptKey,
  isCustomerForecastWithDiffLastMonthKey,
} from '../../interface/tableContentHeaders';
import { TableContentItem } from '../../interface/tableContentItems';
import { convertIDateToDateFormat, convertUnixTimeToIDate } from '../../utils/dateFormat';
import { IYearMonth } from '@/coreDomainsModule/date';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';
import { UserAccount } from '@/coreDomainsModule/entities/users';

/**
 * 客先内示のテーブル表示に使うロジック
 */
export class ForecastForReceiptTableContentViewModel extends ForecastTableContentViewModel {
  /**
   * 客先内示の項目表示用
   * @param headerKey - ヘッダーキー
   * @param item - テーブルコンテンツアイテム
   * @param forecastStartYearMonth - 内示の開始年月
   */
  public displayForecastForReceiptItem = (
    headerKey: ForecastForReceiptHeaderKey,
    item: TableContentItem,
    forecastStartYearMonth?: IYearMonth
  ): string => {
    switch (headerKey) {
      case 'ForecastForReceiptStaff':
        // Handle staff filter table (forecastStaffName is a string)
        if (item.forecastStaffName) {
          return item.forecastStaffName;
        }
        // Handle search result table (staffNames is an array of strings)
        if (item.staffNames && Array.isArray(item.staffNames)) {
          return item.staffNames.join(', ') || '--';
        }
        // Handle other tables (customerForecastStaffList is an array of UserAccount)
        return (
          item.forecastForReceiptResponsibleUsers
            ?.map((user: UserAccount) => UserAccountService.getDisplayName(UserAccountFactory.create(user)))
            .join(', ') ?? '--'
        );
      case 'ForecastForReceiptStaffFull':
        return (
          item.baseBusinessResponsibleUsers
            ?.map((user: UserAccount) => UserAccountService.getDisplayName(UserAccountFactory.create(user)))
            .join(', ') ?? '--'
        );
      case 'ForecastForReceiptRecoverStatus':
        // 受領用内示一覧テーブル: アイコンを表示するのでvue側で処理
        if (item.forecastForReceiptId) {
          return '';
        }
        // 担当者で絞り込みテーブル: テキスト表示
        return `${item.recoveredForecastCount ?? 0}/${item.totalForecastCount ?? 0}`;
      case 'ForecastForReceiptRecoverRate': {
        const recovered = item.recoveredForecastCount ?? 0;
        const total = item.totalForecastCount ?? 0;
        const rate = this._calcRecoveryRate(recovered, total);
        // 受領用内示一覧テーブル: 100%（1000/1000件）形式
        if (item.forecastForReceiptId) {
          return `${rate}%（${recovered}/${total}件）`;
        }
        // 担当者で絞り込みテーブル
        return `${rate}%`;
      }
      case 'ForecastForReceiptLatestUpdateAt':
        if (item.latestUpdateAt !== undefined && item.latestUpdateAt !== null) {
          // 担当者テーブル: latestUpdateAtはIDate型
          if (typeof item.latestUpdateAt === 'object') {
            return convertIDateToDateFormat(item.latestUpdateAt);
          }
          // 検索結果テーブル: latestUpdateAtはmsタイムスタンプ（number型）
          return convertIDateToDateFormat(convertUnixTimeToIDate(item.latestUpdateAt));
        }
        // Handle other tables (nested in customerForecasts)
        return !!item.customerForecasts && item.customerForecasts.length > 0 && item.customerForecasts[0].updateAt
          ? convertIDateToDateFormat(convertUnixTimeToIDate(item.customerForecasts[0].updateAt))
          : '';
      case 'ForecastForReceiptKeyword':
        return item.keyword ?? '';
      case 'customerForecastUpdateAt': {
        if (!item.customerForecasts || item.customerForecasts.length === 0) return '';
        const latestUpdateAt = item.customerForecasts.reduce((max, current) =>
          (current.customerForecastUpdateAt ?? 0) > max.customerForecastUpdateAt ? current : max
        ).customerForecastUpdateAt;
        if (!latestUpdateAt) return '';
        return convertIDateToDateFormat(convertUnixTimeToIDate(latestUpdateAt));
      }
      case 'ForecastForReceiptName':
        return item.forecastForReceiptName ?? '';
      case 'ForecastForReceiptResponsibleUser':
        console.log(item.forecastForReceiptResponsibleUsers);
        return item.forecastForReceiptResponsibleUsers
          ?.map((user: UserAccount) => UserAccountService.getDisplayName(UserAccountFactory.create(user)))
          .join(', ');
      case 'ForecastForReceiptReceiveDate': {
        if (!item.customerForecasts || item.customerForecasts.length === 0) return '';
        const latestUpdateAt = item.customerForecasts.reduce((max, current) =>
          (current.updateAt ?? 0) > max.updateAt ? current : max
        ).updateAt;
        if (!latestUpdateAt) return '';
        return convertIDateToDateFormat(convertUnixTimeToIDate(latestUpdateAt));
      }
      case 'SupplierForecastCreateStatus':
        return ''; // アイコンを表示するのでvue側で処理
      case 'timelineButton':
        return ''; // アイコンを表示するのでvue側で処理
      case 'ForecastForReceiptRowMenu':
        return ''; // vue側でメニューを表示
      default:
        if (isCustomerAnnounceForReceiptKey(headerKey)) {
          return this._displayForecastItem(this._getForecastIndex(headerKey), item, forecastStartYearMonth);
        }
        return '';
    }
  };

  /**
   * ヘッダーのテキストを取得する
   * @param headerKey - ヘッダーキー
   * @param forecastStartYearMonth - 内示の開始年月
   */
  public getHeaderText = (headerKey: ForecastForReceiptHeaderKey, forecastStartYearMonth?: IYearMonth): string => {
    if (isCustomerAnnounceForReceiptKey(headerKey) || isCustomerForecastWithDiffLastMonthKey(headerKey)) {
      return this._forecastHeader(this._getForecastIndex(headerKey), forecastStartYearMonth);
    }
    if (headerKey === 'timelineButton') return '';
    if (headerKey === 'ForecastForReceiptRowMenu') return '';
    if (headerKey === 'removeFromForecastButton') return '';
    return headerKey;
  };

  /**
   * 仕入先内示作成ステータスを取得する
   * TODO: 要確認、仕入先内示が作成されているかどうかの判定としている
   * @param item -
   */
  public isCreatedSupplierForecast = (item: TableContentItem): boolean => {
    return item.businessForecastState === 'supplierForecastCompleted' || item.businessForecastState === 'completed';
  };

  /**
   * 回収率を計算する
   * @param recovered - 回収数
   * @param total - 合計数
   * @returns number - 回収率
   */
  _calcRecoveryRate = (recovered: number, total: number): number => {
    if (total === 0) {
      return 0;
    }
    return Math.round((recovered / total) * 100);
  };

  /**
   * 内示列の表示内容
   * @param index - 内示のインデックス
   * @param item - テーブルの表示内容
   * @param forecastStartYearMonth - 内示の開始年月
   */
  _displayForecastItem = (index: number, item: TableContentItem, forecastStartYearMonth?: IYearMonth): string => {
    const customerAnnounce = item.customerForecasts;
    if (!customerAnnounce) return '';

    const date = this.getIYearMonth(
      forecastStartYearMonth ? new Date(forecastStartYearMonth.year, forecastStartYearMonth.month - 1) : new Date(),
      index
    );
    const yearMonthInfo = { year: date.year, month: date.month };
    const announceIndex = customerAnnounce.findIndex(
      (item) => item.targetYear === yearMonthInfo.year && item.targetMonth === yearMonthInfo.month
    );
    if (announceIndex === -1) return '-';
    return customerAnnounce[announceIndex].amount.addComma();
  };
}
